import React, { useState, useEffect, createContext, useCallback, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { loadUserpilot, rightsToUserpilot, licensesToUserpilot } from '@common/userpilot';
import { useLocation } from 'react-router-dom';

import { GetLicensedApplications } from '@resources/configuration.graphql';

const UserPilotContext = createContext();

const useInitUserPilot = currentUser => {
  const location = useLocation();
  const [userpilot, setUserpilot] = useState(null);

  /**
   * Only run once: asynchronously inject the
   * userpilot script, and continue with setup
   * only if the file loads successfully.
   *
   * https://docs.userpilot.com/article/22-install-userpilot-on-single-page-application-frameworks
   */
  useEffect(() => {
    const initUserPilot = async () => {
      try {
        const windowUserPilot = await loadUserpilot();
        setUserpilot(windowUserPilot);
      } catch (e) {
        console.log(e);
      }
    };
    initUserPilot();
  }, []);

  /**
   * Initial call to identify the current user with userPilot.
   */
  useEffect(() => {
    if (userpilot && currentUser) {
      const { organizationId, userId, username, organizationName, organizationState } =
        currentUser;
      userpilot.identify(`${organizationId}.${userId}`, {
        userId,
        email: username,
        company: {
          id: organizationId,
          name: organizationName,
          state: organizationState
        }
      });
    }
  }, [userpilot, currentUser]);

  /**
   * Ensure that userPilot is updated when the url changes.
   */
  useEffect(() => window.userpilot?.reload(), [location]);

  const updateUserPilot = useCallback(
    data => {
      if (userpilot && currentUser && data) {
        const { organizationId, userId } = currentUser;
        userpilot.identify(`${organizationId}.${userId}`, data);
      }
    },
    [userpilot, currentUser]
  );

  return updateUserPilot;
};

export const UserPilotProvider = ({ currentUser, rights, children }) => {
  const updateUserPilot = useInitUserPilot(currentUser);
  const { data } = useQuery(GetLicensedApplications);

  useEffect(() => {
    if (rights) {
      updateUserPilot(rightsToUserpilot(rights));
    }
  }, [rights, updateUserPilot]);

  useEffect(() => {
    if (data?.licensedApplications) {
      updateUserPilot(licensesToUserpilot(data.licensedApplications));
    }
  }, [data, updateUserPilot]);

  return (
    <UserPilotContext.Provider value={updateUserPilot}>
      {children}
    </UserPilotContext.Provider>
  );
};

export const useUserPilot = () => {
  return useContext(UserPilotContext);
};
